/**
 * `/textbook`'s vocabulary for §5.2's import lifecycle (#144, sub-issue of
 * #76, Track C1).
 *
 * Same exhaustiveness discipline as `./status-labels.ts` and
 * `../features/daily-plan/assistance-language.ts`'s `nextHelp` map: the type
 * is derived from the generated contract (`ImportRunsResponse['runs'][number]
 * ['lifecycleState']`), not retyped by hand, so a seventh lifecycle state
 * added to the schema fails this file at compile time — `Record<K, string>`
 * requires every member of `K` present — rather than rendering `undefined`
 * the first time a real import reaches it.
 *
 * The state is shown in words because CLAUDE.md's hard rule forbids a
 * percentage or a progress bar standing in for one — "No percentage,
 * anywhere, ever." The fuller gloss, "Progress through the lifecycle is a
 * named state and a count of pages, never a bar with a number on it," is
 * issue #144's own body, not a CLAUDE.md quotation — corrected in the #144
 * fix pass (invariant audit) after being misattributed here. The count
 * itself is `textbook-page.tsx`'s job, read directly off
 * `pageCount`/`heldPageCount` — nothing here turns either into a fraction of
 * the other.
 */

import type { ImportRunsResponse } from '@davinci/contracts';
import type { UiLocale } from './locale.js';

type ImportRun = ImportRunsResponse['runs'][number];

export type ImportLifecycleState = ImportRun['lifecycleState'];
export type ImportFailureKind = NonNullable<ImportRun['failureKind']>;

const IMPORT_LIFECYCLE_SENTENCES: Readonly<Record<ImportLifecycleState, string>> = {
  uploaded: 'Uploaded. Waiting to begin extraction.',
  extracting: 'Extracting pages from the file.',
  candidate_review: 'Extraction is complete. The proposed structure is waiting for review.',
  ready_to_publish: 'Reviewed and ready to publish.',
  published: 'Published.',
  failed: 'This import has failed.',
  // #373: the learner withdrew consent before it started. Not a failure.
  cancelled: 'Cancelled before it started, because you withdrew your consent.',
};

const IMPORT_LIFECYCLE_SENTENCES_HE: Readonly<Record<ImportLifecycleState, string>> = {
  uploaded: 'הועלה. ממתין להתחלת החילוץ.',
  extracting: 'מחלץ עמודים מהקובץ.',
  candidate_review: 'החילוץ הסתיים. המבנה המוצע ממתין לבדיקה.',
  ready_to_publish: 'נבדק ומוכן לפרסום.',
  published: 'פורסם.',
  failed: 'הייבוא הזה נכשל.',
  cancelled: 'בוטל לפני שהתחיל, כי ביטלת את ההסכמה.',
};

export function importLifecycleSentence(state: ImportLifecycleState, locale: UiLocale = 'en'): string {
  return (locale === 'he' ? IMPORT_LIFECYCLE_SENTENCES_HE : IMPORT_LIFECYCLE_SENTENCES)[state];
}

/**
 * §5.2's four failure kinds — the pipeline stages that can fail, never a
 * string a model composes (schema.ts's own comment on `IMPORT_FAILURE_KINDS`:
 * "a check the check names, not a model").
 *
 * A `switch`, not a `Record`, for the same reason
 * `../features/progress/evidence-language.ts`'s `verdictText` is one rather
 * than `VERDICT_LABELS`' `Record`: `failureKind` is a NULLABLE enum in the
 * schema, and the generator's handling of `enum: [...values, null]` on a
 * nullable field emits BOTH the real `null` type and the literal string
 * `'null'` into the generated union (`generated/types.ts`) — a `Record`
 * cannot take `null` as a key at all, and `NonNullable<...>` strips only the
 * real `null`, leaving `'null'` behind as a member this map would still have
 * to satisfy. The `case 'null':` arm below is that generator artifact, never
 * reachable through real data: `failureKind` is NOT NULL in the database
 * exactly when `lifecycleState = 'failed'` (`import_runs_failure_iff_failed`,
 * migration 0017), the only state this function is ever called for.
 */
export function importFailureKindLabel(kind: ImportFailureKind, locale: UiLocale = 'en'): string {
  if (locale === 'he') {
    switch (kind) {
      case 'rasterisation_failed':
        return 'יצירת תמונה של העמוד נכשלה.';
      case 'ocr_failed':
        return 'קריאת הטקסט שבעמוד נכשלה.';
      case 'structure_proposal_failed':
        return 'הצעת המבנה של הספר נכשלה.';
      case 'manual_fallback_required':
        return 'צריך לסיים את זה ידנית.';
      case 'null':
        return 'לא נרשם כשל.';
    }
  }
  switch (kind) {
    case 'rasterisation_failed':
      return 'Rendering a page image failed.';
    case 'ocr_failed':
      return 'Reading the text on a page failed.';
    case 'structure_proposal_failed':
      return "Proposing the book's structure failed.";
    case 'manual_fallback_required':
      return 'This needs to be finished by hand.';
    case 'null':
      return 'No failure was recorded.';
  }
}
